import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';

// Add custom type for directory input
declare module 'react' {
  interface InputHTMLAttributes<T> extends HTMLAttributes<T> {
    directory?: string;
    webkitdirectory?: string;
  }
}

interface DragDropUploadProps {
  onFilesSelected: (files: File[]) => void;
  acceptedFileTypes?: string[] | string;
  multiple?: boolean;
  className?: string;
  label?: string;
  maxFiles?: number;
  maxSizeMB?: number;
  customFormatDescription?: React.ReactNode;
  disabled?: boolean;
}

const DragDropUpload: React.FC<DragDropUploadProps> = ({
  onFilesSelected,
  acceptedFileTypes = ['*'],
  multiple = true,
  className = '',
  label = 'Trascina i file o cartelle qui, oppure clicca per selezionarli',
  maxFiles = 0, // 0 means unlimited
  maxSizeMB = 0, // 0 means unlimited
  customFormatDescription,
  disabled = false
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Convert acceptedFileTypes to array if it's a string
  const normalizedAcceptedTypes = typeof acceptedFileTypes === 'string'
    ? acceptedFileTypes.split(',')
    : acceptedFileTypes;

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const validateFiles = useCallback((files: File[]): File[] => {
    setErrorMessage(null);
    let validFiles = [...files];
    
    // Check file extensions if acceptedFileTypes are provided
    if (normalizedAcceptedTypes && normalizedAcceptedTypes.length > 0 && normalizedAcceptedTypes[0] !== '*') {
      validFiles = validFiles.filter(file => {
        const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
        return normalizedAcceptedTypes.some(type => 
          type === '*' || type.toLowerCase() === fileExtension.toLowerCase()
        );
      });
      
      if (validFiles.length < files.length) {
        setErrorMessage(`Alcuni file sono stati ignorati. Tipi accettati: ${normalizedAcceptedTypes.join(', ')}`);
      }
    }
    
    // Check max file count
    if (maxFiles > 0 && validFiles.length > maxFiles) {
      validFiles = validFiles.slice(0, maxFiles);
      setErrorMessage(`Numero massimo di file: ${maxFiles}. Solo i primi ${maxFiles} verranno utilizzati.`);
    }
    
    // Check file sizes
    if (maxSizeMB > 0) {
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      const oversizedFiles = validFiles.filter(file => file.size > maxSizeBytes);
      
      if (oversizedFiles.length > 0) {
        validFiles = validFiles.filter(file => file.size <= maxSizeBytes);
        setErrorMessage(`Alcuni file superano la dimensione massima di ${maxSizeMB} MB e sono stati ignorati.`);
      }
    }
    
    return validFiles;
  }, [normalizedAcceptedTypes, maxFiles, maxSizeMB]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const items = e.dataTransfer.items;
    const files: File[] = [];
    let processedItems = 0;
    let totalItems = 0;

    // Process dropped items (files or folders)
    if (items && items.length > 0) {
      totalItems = items.length;
      
      const traverseFileTree = async (item: any, path = '') => {
        if (item.isFile) {
          // Get file
          item.file((file: File) => {
            // Add path info to file object
            Object.defineProperty(file, 'path', {
              value: path + file.name,
              writable: true
            });
            files.push(file);
            
            processedItems++;
            // If all items are processed, call the callback
            if (processedItems === totalItems && files.length > 0) {
              const validFiles = validateFiles(files);
              onFilesSelected(validFiles);
            }
          });
        } else if (item.isDirectory) {
          // Get folder contents
          const dirReader = item.createReader();
          
          // Read all entries in the directory
          const readAllEntries = async () => {
            const readEntries = () => {
              dirReader.readEntries(async (entries: any[]) => {
                if (entries.length > 0) {
                  for (const entry of entries) {
                    await traverseFileTree(entry, path + item.name + '/');
                  }
                  // Continue reading if there might be more entries
                  readEntries();
                } else {
                  // This directory is fully processed
                  processedItems++;
                  // If all items are processed, call the callback
                  if (processedItems === totalItems && files.length > 0) {
                    const validFiles = validateFiles(files);
                    onFilesSelected(validFiles);
                  }
                }
              });
            };
            readEntries();
          };
          
          await readAllEntries();
        }
      };

      // Process all items
      const processItems = async () => {
        for (let i = 0; i < items.length; i++) {
          const item = items[i].webkitGetAsEntry();
          if (item) {
            await traverseFileTree(item);
          } else {
            // If item couldn't be processed, count it as processed
            processedItems++;
          }
        }
      };

      processItems();
    } else if (e.dataTransfer.files.length > 0) {
      // Fallback for browsers that don't support DataTransferItemList
      const droppedFiles = Array.from(e.dataTransfer.files);
      const validFiles = validateFiles(droppedFiles);
      onFilesSelected(validFiles);
    }
  }, [onFilesSelected, validateFiles]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      const validFiles = validateFiles(selectedFiles);
      onFilesSelected(validFiles);
    }
  }, [onFilesSelected, validateFiles]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="space-y-2">
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          disabled ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60' :
          isDragging ? 'border-primary-500 bg-primary-50 cursor-pointer' : 'border-gray-300 hover:border-primary-400 cursor-pointer'
        } ${className}`}
        onDragOver={disabled ? undefined : handleDragOver}
        onDragLeave={disabled ? undefined : handleDragLeave}
        onDrop={disabled ? undefined : handleDrop}
        onClick={disabled ? undefined : handleClick}
      >
        <div className="flex flex-col items-center justify-center space-y-2">
          <Upload className="h-10 w-10 text-gray-400" />
          <p className="text-sm text-gray-500">{label}</p>
          {customFormatDescription ? (
            <div className="mt-1 text-xs text-gray-400">
              {customFormatDescription}
            </div>
          ) : (
            (maxFiles > 0 || maxSizeMB > 0 || (normalizedAcceptedTypes && normalizedAcceptedTypes[0] !== '*')) && (
              <div className="mt-1 text-xs text-gray-400">
                {normalizedAcceptedTypes && normalizedAcceptedTypes[0] !== '*' && (
                  <p>{t('dragDropUpload.acceptedTypes', { types: normalizedAcceptedTypes.join(', ') })}</p>
                )}
                {maxFiles > 0 && <p>{t('dragDropUpload.maxFiles', { count: maxFiles })}</p>}
                {maxSizeMB > 0 && <p>{t('dragDropUpload.maxSize', { size: maxSizeMB })}</p>}
              </div>
            )
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={normalizedAcceptedTypes.join(',')}
          multiple={multiple}
          webkitdirectory=""
          directory=""
          onChange={handleFileInputChange}
        />
      </div>
      {errorMessage && (
        <div className="text-sm text-orange-500 mt-1">{errorMessage}</div>
      )}
    </div>
  );
};

export default DragDropUpload; 